import { AssetGroup } from '../core/asset/AssetGroup';
import { CircleAsset } from '../core/asset/CircleAsset';
import { ColorAsset } from '../core/asset/ColorAsset';
import { GradientAsset } from '../core/asset/GradientAsset';
import { IconAsset } from '../core/asset/IconAsset';
import { LottieAsset } from '../core/asset/LottieAsset/LottieAsset';
import { RectangleAsset } from '../core/asset/RectangleAsset';
import { TimeAwareMediaAsset } from '../core/asset/TimeAwareMediaAsset';
import type { GetAssetType, AssetType, Asset } from '../core/asset/BaseAsset';
import { assertHasOwnProperties, assertInEnum, assertObject } from '../validators';
import { Config } from '../video-data/config';
import { TextAsset } from '../core/asset/TextAsset';
import { FancyCaptionAsset } from '../core/asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import type { Slide } from '../core/slide/Slide';
import type { Subslide } from '../core/slide/Subslide';

function getAssetForType<T extends AssetType>(assetType: T, parent, settings): GetAssetType<T> {
    switch (assetType) {
        case Config.GROUP_ASSET_TYPE:
            return new AssetGroup(parent, settings) as GetAssetType<T>;
        case Config.COLOR_ASSET_TYPE:
            return new ColorAsset(parent, settings) as GetAssetType<T>;
        case Config.CIRCLE_ASSET_TYPE:
            return new CircleAsset(parent, settings) as GetAssetType<T>;
        case Config.GRADIENT_ASSET_TYPE:
            return new GradientAsset(parent, settings) as GetAssetType<T>;
        case Config.RECTANGLE_ASSET_TYPE:
            return new RectangleAsset(parent, settings) as GetAssetType<T>;
        case Config.TEXT_ASSET_TYPE:
            return new TextAsset(parent, settings) as GetAssetType<T>;
        case Config.FANCY_CAPTION_ASSET_TYPE:
            return new FancyCaptionAsset(parent, settings) as GetAssetType<T>;
        case Config.MEDIA_ASSET_TYPE:
            return new TimeAwareMediaAsset(parent, settings) as GetAssetType<T>;
        case Config.LOTTIE_ASSET_TYPE:
            return new LottieAsset(parent, settings) as GetAssetType<T>;
        case Config.ICON_ASSET_TYPE:
            return new IconAsset(parent, settings) as GetAssetType<T>;
        default:
            throw Error(`Unable to create asset ${assetType}`);
    }
}

export function addAssetToContainer<T extends AssetType>(
    parent,
    assetType: T,
    settings = {},
): GetAssetType<T> {
    assertInEnum(assetType, [
        Config.GROUP_ASSET_TYPE,
        Config.COLOR_ASSET_TYPE,
        Config.CIRCLE_ASSET_TYPE,
        Config.GRADIENT_ASSET_TYPE,
        Config.RECTANGLE_ASSET_TYPE,
        Config.TEXT_ASSET_TYPE,
        Config.FANCY_CAPTION_ASSET_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.LOTTIE_ASSET_TYPE,
        Config.ICON_ASSET_TYPE,
    ]);
    const settingsWithDefaults = {
        updateEventSource: parent,
        updateParentOnRedraw: true, // this should be true for all, except root nodes?
        autoDraw: parent.autoDraw,
        fetchResources: parent.fetchResources,
        ...settings,
    };
    const asset = getAssetForType(assetType, parent, settingsWithDefaults);
    parent._addAsset(asset);
    return asset;
}

export const buildChildrenForSceneUsingData = (slide: Slide | Subslide, data) => {
    assertObject(data);
    assertHasOwnProperties(data, ['children']);

    const scene = Config.isScene(slide) ? slide.scene : slide;
    const initAutoDraw = scene.autoDraw;
    scene.autoDraw = false;

    const childrenOfAsset = { scene: [] };
    const createdAssets: Record<string, Asset> = {};

    // Note that the order of when our components are created is crucial here.
    // We are iterating from leafs to root.
    // Before load data we have to append children to hierarchy
    data.children.reverse().forEach((assetData) => {
        const settings = {
            name: assetData.name,
            id: assetData.id,
            updateEventSource: scene,
            updateParentOnRedraw: true, // this should be true for all, except root nodes?
            autoDraw: false,
            fetchResources: scene.fetchResources,
        };
        const asset = getAssetForType(assetData.classType, scene, settings);

        if (childrenOfAsset[asset.id]) {
            childrenOfAsset[asset.id].forEach((child) => {
                asset._addAsset(child);
            });
        }

        asset.load(assetData);
        createdAssets[assetData.id] = asset;

        const parentId = assetData.parentId || 'scene';
        childrenOfAsset[parentId] = childrenOfAsset[parentId] || [];
        childrenOfAsset[parentId].unshift(asset);
    });

    // append scene level children
    childrenOfAsset['scene'].forEach((child) => {
        scene._addAsset(child);
    });

    // append scene level stickers
    if (Config.isScene(slide) && data.hasOwnProperty('stickers')) {
        data.stickers.forEach((assetData) => {
            const settings = {
                name: assetData.name,
                id: assetData.id,
                updateEventSource: slide.stickers,
                updateParentOnRedraw: true, // this should be true for all, except root nodes?
                autoDraw: false,
                fetchResources: slide.fetchResources,
            };
            const asset = getAssetForType(assetData.classType, slide.stickers, settings);
            asset.load(assetData);
            createdAssets[assetData.id] = asset;

            const parentId = assetData.parentId;
            if (parentId && createdAssets[parentId]) {
                createdAssets[parentId]._addAsset(asset);
            } else {
                slide.stickers._addAsset(asset);
            }
        });
    }

    scene.autoDraw = initAutoDraw;
};
